import { useMemo, useState } from "react";
import { useAction } from "convex/react";
import { api } from "../../../convex/_generated/api";
import type { Id } from "../../../convex/_generated/dataModel";
import {
  computeQuote,
  type ContentSelection,
  type TimeRange,
} from "../../../convex/lib/quote";
import type { ChannelScan } from "../../../convex/lib/scan";
import { ALL_CONTENT, asBoostTier, formatBytes, formatCount } from "../../lib/estimator";
import { ContentToggles, TimeRangeSelector } from "./controls";
import { QuoteReceipt } from "./QuoteReceipt";
import { MediaChooser } from "./MediaChooser";

/** Post-connect estimator: pick real channels → live quote → dynamic checkout. */
export function AppEstimator({
  tenantId,
  channels,
  truncated,
  premiumTier,
}: {
  tenantId: Id<"tenants">;
  channels: ChannelScan[];
  truncated: boolean;
  premiumTier: number | null;
}) {
  const checkout = useAction(api.checkout.createMigrationCheckout);
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(channels.map((c) => c.id)),
  );
  const [content, setContent] = useState<ContentSelection>({ ...ALL_CONTENT });
  const [timeRange, setTimeRange] = useState<TimeRange>({ kind: "all" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [freeStarted, setFreeStarted] = useState(false);

  const channelIds = useMemo(() => [...selected], [selected]);
  const quote = useMemo(
    () =>
      computeQuote({ channelIds, contentTypes: content, timeRange }, channels, {
        uploadLimitTier: asBoostTier(premiumTier),
      }),
    [channelIds, content, timeRange, channels, premiumTier],
  );

  const maxMsgs = useMemo(
    () => Math.max(1, ...channels.map((c) => c.text.allTime.count)),
    [channels],
  );

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  const allOn = selected.size === channels.length;

  async function onCheckout() {
    setBusy(true);
    setError(null);
    try {
      const res = await checkout({
        tenantId,
        selection: { channelIds, contentTypes: content, timeRange },
      });
      if (res.free) {
        setFreeStarted(true);
      } else {
        window.location.href = res.url;
      }
    } catch (e) {
      setError(
        e instanceof Error && /scan/i.test(e.message)
          ? "Scan your workspace first, then try again."
          : "Couldn’t start checkout. Please try again.",
      );
    } finally {
      setBusy(false);
    }
  }

  const nothingPicked = channelIds.length === 0 || quote.lineItems.length === 0;

  return (
    <div className="est">
      <div className="est-controls">
        <div className="est-field-head">
          <span className="eyebrow">Content</span>
        </div>
        <ContentToggles value={content} onChange={setContent} />

        <div className="est-field-head">
          <span className="eyebrow">Time range</span>
        </div>
        <TimeRangeSelector value={timeRange} onChange={setTimeRange} />

        <div className="est-field-head est-field-head-row">
          <span className="eyebrow">Channels</span>
          <button
            type="button"
            className="linklike mono"
            onClick={() =>
              setSelected(allOn ? new Set() : new Set(channels.map((c) => c.id)))
            }
          >
            {allOn ? "Clear all" : "Select all"}
          </button>
        </div>

        <div className="channel-list">
          {channels.map((c) => {
            const msgs = c.text.allTime.count;
            const media =
              c.pics.allTime.bytes + c.video.allTime.bytes + c.files.allTime.bytes;
            return (
              <label className="channel-row" key={c.id}>
                <input
                  type="checkbox"
                  checked={selected.has(c.id)}
                  onChange={() => toggle(c.id)}
                />
                <span className="channel-name">
                  {c.isPrivate ? "🔒" : "#"} {c.name}
                </span>
                <span className="channel-meter" aria-hidden="true">
                  <span style={{ width: `${(msgs / maxMsgs) * 100}%` }} />
                </span>
                <span className="channel-stat mono">
                  {formatCount(msgs)}
                  {media > 0 && <em> · {formatBytes(media)}</em>}
                </span>
              </label>
            );
          })}
        </div>

        {(content.pics || content.video || content.files) && channelIds.length > 0 && (
          <MediaChooser
            tenantId={tenantId}
            channels={channels}
            channelIds={channelIds}
            premiumTier={premiumTier}
          />
        )}
      </div>

      <QuoteReceipt quote={quote} truncated={truncated}>
        {error && <p className="form-error">{error}</p>}
        {freeStarted ? (
          <p className="est-free-note mono">
            ✓ Free proof-of-concept queued — watch it on the live dashboard.
          </p>
        ) : (
          <button
            className="btn btn-primary"
            onClick={onCheckout}
            disabled={busy || nothingPicked}
          >
            {busy
              ? "Starting…"
              : quote.isFree
                ? "Start free proof-of-concept"
                : "Get started"}
          </button>
        )}
        <p className="hint-line mono">
          {quote.isFree
            ? "One channel · last 100 messages · no card"
            : "Secure one-time payment via Stripe"}
        </p>
      </QuoteReceipt>
    </div>
  );
}
